import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { SITE } from "@/data/site";

export default function ThankYou() {
  useEffect(() => {
    document.title = "Thank You | Ardeca Construction";
    return () => { document.title = "Ardeca Construction | Trusted Remodeling in Freehold, NJ"; };
  }, []);

  const ease = [0.22, 1, 0.36, 1];

  return (
    <section
      data-testid="thank-you-page"
      className="relative overflow-hidden bg-brand-darker text-white min-h-[calc(100vh-72px)] flex items-center"
    >
      <Seo title="Thank You | Ardeca Construction" description="Your request has been received. The Ardeca Construction team will be in touch soon." url="/thank-you" noindex />

      {/* decorative glows */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-[420px] w-[420px] rounded-full bg-brand-orange/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-24 h-[420px] w-[420px] rounded-full bg-brand-orange/5 blur-3xl" />

      <div className="container-x relative py-20 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease }}
          className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-5 py-2.5"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-orange opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-orange" />
          </span>
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-white/90">Request Received</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.12, ease }}
          className="mt-10 grid place-items-center h-16 w-16 rounded-full border-2 border-brand-orange text-brand-orange"
        >
          <Check size={30} strokeWidth={2.5} />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18, ease }}
          className="mt-8 font-extrabold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.98] max-w-4xl"
        >
          Thank you. We'll be in touch soon.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.26, ease }}
          className="mt-8 text-lg md:text-xl text-white/55 leading-relaxed max-w-2xl"
        >
          Your request is with our team. We'll follow up within one business day to learn more about your
          project — no pressure, no obligation.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.34, ease }}
          className="mt-11 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
        >
          <Link
            to="/"
            data-testid="thank-you-home-btn"
            className="group inline-flex items-center justify-between gap-6 rounded-full bg-brand-orange pl-8 pr-3 py-3.5 text-sm font-bold uppercase tracking-[0.06em] text-white transition-all duration-300 hover:bg-brand-orangeHover hover:scale-[1.02] active:scale-95"
          >
            Back to Home
            <span className="grid place-items-center h-9 w-9 rounded-full bg-white/20 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
              <ArrowUpRight size={18} />
            </span>
          </Link>

          <a
            href={SITE.phoneHref}
            data-testid="thank-you-call-btn"
            className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/20 px-8 py-3.5 text-sm font-bold tracking-[0.04em] text-white transition-all duration-300 hover:border-brand-orange hover:bg-white/5 hover:scale-[1.02] active:scale-95"
          >
            <Phone size={17} className="text-brand-orange" /> {SITE.phoneDisplay}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
